import { useEffect, useRef } from "react";

import {
  ChatGraphQLError,
  GET_CHAT_ROOM_QUERY,
  GetChatRoomInput,
  GetChatRoomResponse,
  LOG_IN_MESSENGER_MUTATION,
  PostMessengerLoginResponse,
} from "@/utils/GraphQLClient/messenger";

import * as Sentry from "@sentry/react";
import { UseQueryExecute, useMutation, useQuery } from "urql";

import { UNAUTHENTICATED_CODE } from "../utils/GraphQLClient";
import { ChatConfiguration } from "../utils/GraphQLClient/messenger";

const isChatGraphQLError = (e: unknown): e is ChatGraphQLError =>
  !!e && typeof e === "object" && "code" in e && "message" in e;

// Expected outcomes, not bugs: not in the care team, messenger not enabled, and a rejected
// session token, which the GraphQL client hands to the refresh-or-logout handler.
const EXPECTED_CHAT_ERROR_CODES = new Set(["400", "401", "403", UNAUTHENTICATED_CODE]);

/**
 * Log into messenger feature
 * @param messengerEnabled Whether the org has messenger (see `useMessengerEnabled`). The login
 * mutation fires once, the first time this is true, so it waits for the permission to resolve.
 * @returns Client-side JWT token and configuration
 */
export const useMessengerLogin = (
  messengerEnabled: boolean
): {
  token?: string;
  config?: ChatConfiguration;
  loading: boolean;
  error?: ChatGraphQLError;
} => {
  const [{ data, fetching, error }, messengerLogin] =
    useMutation<PostMessengerLoginResponse>(LOG_IN_MESSENGER_MUTATION);
  const hasLoggedIn = useRef(false);

  useEffect(() => {
    if (hasLoggedIn.current || !messengerEnabled) {
      return;
    }
    hasLoggedIn.current = true;

    const handleLogin = async () => {
      const result = await messengerLogin();
      const error = result.error?.graphQLErrors.at(0)?.originalError;
      if (!error) {
        return;
      }
      const mutationErr = isChatGraphQLError(error) ? (error as unknown as ChatGraphQLError) : undefined;
      if (!mutationErr || !EXPECTED_CHAT_ERROR_CODES.has(mutationErr.code)) {
        Sentry.captureException("Messenger: logInToMessenger mutation error", {
          level: "error",
          tags: { component: "usePatientMessenger", feature: "messenger", operation: "LOG_IN_TO_MESSENGER" },
          extra: { error: JSON.stringify(error) },
        });
      }
    };

    handleLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messengerEnabled]);

  // The mutation is triggered in useEffect (after the first render), so
  // `fetching` is false before it fires. Treat the hook as loading until
  // either data or an error has been received.
  const isLoading = fetching || (!data && !error);

  if (!messengerEnabled) {
    return { token: undefined, config: undefined, loading: false };
  }

  if (error) {
    const err = error?.graphQLErrors.at(0)?.originalError;
    const chatError = isChatGraphQLError(err) ? (err as unknown as ChatGraphQLError) : undefined;
    return {
      token: undefined,
      config: undefined,
      loading: false,
      ...(chatError && {
        error: chatError,
      }),
    };
  }

  const chatSession = data?.initiateMessengerAuth;
  const response = {
    token: chatSession?.token,
    loading: isLoading,
    config: chatSession?.config,
  };

  return response;
};

/**
 * Retrieves a patient's chat room
 * @param fhir_patient_id The patient whose room to look up; the query pauses without one
 * @param messengerEnabled Whether the org has messenger (see `useMessengerEnabled`); the query
 * pauses and the hook returns an empty, non-loading result while false
 * @returns Chat room JID, if found.
 */
export const usePatientChatRoom = (
  fhir_patient_id: string | undefined,
  messengerEnabled: boolean
): { loading: boolean; room_jid?: string; reexecute_query?: UseQueryExecute; error?: ChatGraphQLError } => {
  const [{ data: chatRoomData, fetching, error }, reexecute_query] = useQuery<
    GetChatRoomResponse,
    { input: GetChatRoomInput }
  >({
    query: GET_CHAT_ROOM_QUERY,
    variables: {
      input: {
        fhir_patient_id: fhir_patient_id ?? "",
      },
    },
    pause: !fhir_patient_id || !messengerEnabled,
    requestPolicy: "cache-and-network",
  });

  if (!messengerEnabled) {
    return {
      room_jid: undefined,
      loading: false,
    };
  }
  if (error) {
    const err = error?.graphQLErrors.at(0)?.originalError;
    const chatRoomError = isChatGraphQLError(err) ? (err as unknown as ChatGraphQLError) : undefined;

    if (err && (!chatRoomError || !EXPECTED_CHAT_ERROR_CODES.has(chatRoomError.code))) {
      Sentry.captureException("Messenger: getChatRoom query error", {
        level: "error",
        tags: { component: "usePatientMessenger", feature: "messenger", operation: "GET_CHAT_ROOM" },
        extra: { error: JSON.stringify(error.graphQLErrors) },
      });
    }

    return {
      room_jid: undefined,
      loading: fetching,
      ...(chatRoomError && {
        error: chatRoomError,
      }),
    };
  }
  return {
    room_jid: chatRoomData?.getChatRoom?.name,
    loading: fetching,
    reexecute_query,
  };
};
